import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal, Pencil, Trash2, FolderInput, ChevronRight } from 'lucide-react';
import useBoardStore from '../../../store/boardStore';

const MENU_WIDTH = 176; // w-44

// Menu de opções da PASTA de workspace. Pode abrir pelo botão "..." (uncontrolled)
// ou por clique-direito no header (controlled via anchorPoint).
// Reusa createPortal pra escapar o stacking context da sidebar.
export default function WorkspaceFolderMenu({
  folder,
  onRename,
  onDelete,
  anchorPoint = null,
  onClose,
  hideButton = false,
}) {
  const { workspaces, moveFolderToWorkspace } = useBoardStore();
  const [openUncontrolled, setOpenUncontrolled] = useState(false);
  const controlled = anchorPoint != null;
  const open = controlled ? true : openUncontrolled;
  const setOpen = (v) => {
    if (controlled) {
      const next = typeof v === 'function' ? v(true) : v;
      if (!next) onClose?.();
    } else {
      setOpenUncontrolled(v);
    }
  };
  const [moving, setMoving] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const btnRectRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;
    const recompute = () => {
      const popHeight = popRef.current?.offsetHeight || 0;
      const popWidth = popRef.current?.offsetWidth || MENU_WIDTH;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (controlled) {
        const x = anchorPoint.x;
        const y = anchorPoint.y;
        let left = x;
        if (left + popWidth > vw - 8) left = Math.max(8, x - popWidth);
        let top = y;
        if (popHeight > 0 && top + popHeight > vh - 8) top = Math.max(8, y - popHeight);
        setPos({ top: top + window.scrollY, left: left + window.scrollX });
        return;
      }
      if (!btnRef.current) return;
      const live = btnRef.current.getBoundingClientRect();
      if (live.width > 0) btnRectRef.current = live;
      const rect = btnRectRef.current;
      if (!rect) return;
      const spaceBelow = vh - rect.bottom;
      const flipUp = popHeight > 0 && spaceBelow < (popHeight + 8) && rect.top > spaceBelow;
      const top = flipUp
        ? rect.top + window.scrollY - popHeight - 4
        : rect.bottom + window.scrollY + 4;
      const left = rect.right + window.scrollX - MENU_WIDTH;
      setPos({ top, left });
    };
    recompute();
    const raf = requestAnimationFrame(recompute);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open, moving, controlled, anchorPoint]);

  useEffect(() => {
    if (!open) { btnRectRef.current = null; return; }
    const h = (e) => {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inBtn && !inPop) { setOpen(false); setMoving(false); }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const closeAll = () => { setOpen(false); setMoving(false); };

  const doMove = async (ws) => {
    closeAll();
    try {
      await moveFolderToWorkspace(folder.id, ws.id);
    } catch (err) {
      // erro tratado no chamador via dialog se quiser; aqui só não quebra
      console.error('moveFolderToWorkspace error:', err?.message);
    }
  };

  const otherWorkspaces = workspaces.filter((w) => w.id !== folder.workspace_id);

  const popup = open ? (
    <div
      ref={popRef}
      style={{ position: 'absolute', top: pos.top, left: pos.left, width: MENU_WIDTH, zIndex: 9999 }}
      className="bg-card border border-border rounded-lg shadow-xl py-1"
    >
      {!moving ? (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onRename?.(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5"
          >
            <Pencil className="w-3.5 h-3.5" /> Renomear
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setMoving(true); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5"
          >
            <FolderInput className="w-3.5 h-3.5" /> Mover para área de trabalho
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete?.(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger hover:bg-danger/10"
          >
            <Trash2 className="w-3.5 h-3.5" /> Excluir pasta
          </button>
        </>
      ) : (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setMoving(false); }}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-text-secondary hover:bg-white/5"
          >
            <ChevronRight className="w-3 h-3 rotate-180" /> Voltar
          </button>
          <div className="border-t border-border/40 my-0.5" />
          <div className="max-h-60 overflow-y-auto">
            {otherWorkspaces.length === 0 ? (
              <div className="px-3 py-2 text-xs text-text-secondary">Nenhuma outra área</div>
            ) : (
              otherWorkspaces.map((ws) => (
                <button
                  key={ws.id}
                  onClick={(e) => { e.stopPropagation(); doMove(ws); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/5"
                >
                  <span className="truncate">{ws.name}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <>
      {!hideButton && (
        <button
          ref={btnRef}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); setMoving(false); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="p-0.5 rounded opacity-0 group-hover/f:opacity-100 hover:bg-white/10 hover:text-text-primary transition-all"
          title="Opções"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
      )}
      {popup && createPortal(popup, document.body)}
    </>
  );
}
